import { useCallback, useState } from 'react';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { stanNotify } from '@/lib/stanNotify';
import { openOAuthPopup } from '@/lib/utils/popupWindow';
import { getPayPalAuthUrl, getStripeAuthUrl } from '@/lib/api/payments';
import { useUser } from '@/queries/useUser';
import { useDisconnectPayPalMutation, useDisconnectStripeMutation } from '../queries/usePaymentMutations';
import { mainApi } from '@/lib/api';

interface UsePaymentIntegrationsProps {
    refetchIntegrations: () => void;
}

export const usePaymentIntegrations = ({ refetchIntegrations }: UsePaymentIntegrationsProps) => {
    const { user } = useUser();
    const [loading, setLoading] = useState(false);
    const [showProductWarningModal, setShowProductWarningModal] = useState(false);
    const [pendingConnection, setPendingConnection] = useState<(() => void) | null>(null);

    const { mutateAsync: disconnectStripeMutation, isPending: isDisconnectingStripe } = useDisconnectStripeMutation();
    const { mutateAsync: disconnectPayPalMutation, isPending: isDisconnectingPayPal } = useDisconnectPayPalMutation();

    // Connect Stripe - opens /integration-authorization?connect=stripe popup
    const connectStripe = useCallback(() => {
        const url = getStripeAuthUrl();
        openOAuthPopup(
            url,
            'Connect Stripe Integration',
            () => {
                refetchIntegrations();
                stanAnalytics('stripe-connected', {
                    meta: { user_id: user?.user_id, username: user?.username },
                });
            },
            500,
            800
        );
    }, [refetchIntegrations, user]);

    // Connect PayPal - opens popup
    const connectPayPal = useCallback(() => {
        const currentUrl = window.location.href;
        const returnUrl = currentUrl.split('?')[0] + '?tab=store';
        const authUrl = getPayPalAuthUrl(returnUrl);

        openOAuthPopup(
            authUrl,
            'Connect PayPal Integration',
            () => {
                refetchIntegrations();
                stanAnalytics('paypal-connected', {
                    meta: { user_id: user?.user_id, username: user?.username },
                });
            },
            500,
            800
        );
    }, [refetchIntegrations, user]);

    // Manage Stripe - calls API directly to get onboarding URL
    const manageStripe = useCallback(async () => {
        try {
            setLoading(true);
            // Call API to get Stripe dashboard/onboarding URL with returnUrl
            const returnUrl = window.location.origin + '/integration-authorization?closeWindow=true';
            const response = await mainApi.post('v1/integrations/stripe/connect', { returnUrl });

            if (response.data.url) {
                openOAuthPopup(
                    response.data.url,
                    'Manage Stripe Account',
                    () => {
                        refetchIntegrations();
                    },
                    500,
                    800
                );
            }
        } catch (error: any) {
            console.error('Manage Stripe error:', error);
            stanNotify({
                type: 'error',
                title: 'Something went wrong',
                text: error?.response?.data?.message ?? 'Please try again later.',
            });
        } finally {
            setLoading(false);
        }
    }, [refetchIntegrations]);

    // Connect Adyen - opens /integration-authorization?connect=adyen popup
    const connectAdyen = useCallback(() => {
        const url = '/integration-authorization?connect=adyen';
        openOAuthPopup(
            url,
            'Connect Adyen Integration',
            () => {
                refetchIntegrations();
                stanAnalytics('adyen-connected', {
                    meta: { user_id: user?.user_id, username: user?.username },
                });
            },
            500,
            800
        );
    }, [refetchIntegrations, user]);

    // Manage Adyen - calls API directly to get management URL
    const manageAdyen = useCallback(async () => {
        try {
            setLoading(true);
            const response = await mainApi.post('v1/integrations/adyen', {});

            if (response.data.url) {
                openOAuthPopup(
                    response.data.url,
                    'Manage Adyen Account',
                    () => {
                        refetchIntegrations();
                    },
                    500,
                    800
                );
            }
        } catch (error: any) {
            console.error('Manage Adyen error:', error);
            stanNotify({
                type: 'error',
                title: 'Something went wrong',
                text: error?.response?.data?.message ?? 'Please try again later.',
            });
        } finally {
            setLoading(false);
        }
    }, [refetchIntegrations]);

    // Disconnect Stripe
    const handleDisconnectStripe = useCallback(async () => {
        await disconnectStripeMutation();
        await refetchIntegrations();
    }, [disconnectStripeMutation, refetchIntegrations]);

    // Disconnect PayPal
    const handleDisconnectPayPal = useCallback(
        async (integrationId: number) => {
            await disconnectPayPalMutation(integrationId);
            await refetchIntegrations();
        },
        [disconnectPayPalMutation, refetchIntegrations]
    );

    // Handle payment processor connection with product warning check
    const handlePaymentProcessorConnect = useCallback((connectFn: () => void, hasProducts: boolean) => {
        if (!hasProducts) {
            setPendingConnection(() => connectFn);
            setShowProductWarningModal(true);
        } else {
            connectFn();
        }
    }, []);

    const handleProductWarningContinue = useCallback(() => {
        setShowProductWarningModal(false);
        if (pendingConnection) {
            pendingConnection();
            setPendingConnection(null);
        }
    }, [pendingConnection]);

    const handleProductWarningCancel = useCallback(() => {
        setShowProductWarningModal(false);
        setPendingConnection(null);
    }, []);

    return {
        loading: loading || isDisconnectingStripe || isDisconnectingPayPal,
        showProductWarningModal,
        connectStripe,
        connectPayPal,
        connectAdyen,
        manageStripe,
        manageAdyen,
        handleDisconnectStripe,
        handleDisconnectPayPal,
        handlePaymentProcessorConnect,
        handleProductWarningContinue,
        handleProductWarningCancel,
    };
};
